import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import AnimateHeight from 'react-animate-height';

import { HeaderLinkList } from './types';
import Link, { StyledLink } from './Link';
import { HEADER_VARIABLES } from './Header';

import LinkChevron from '../assets/linkChevron.inline.svg';

import { filterProps } from 'utils/helpers';

interface ILinksList extends Omit<HeaderLinkList, 'type'> {
  open: boolean;
  onClick: JSX.IntrinsicElements['button']['onClick'];
}

const Container = styled.li`
  ${({ theme: { colors, durations, mq } }) => css`
    display: flex;
    position: relative;

    &:hover {
      & > div > div > ul {
        opacity: 1;
        pointer-events: initial;
      }

      > a,
      > span {
        &::after {
          left: 0;
          opacity: 1;
          margin: 0 2rem;
          border-bottom: 3px solid ${colors.primary};
          width: calc(100% - (2rem * 2));
        }
      }
    }

    > a,
    > span {
      &::after {
        display: block;
        position: absolute;
        bottom: -2.25rem;
        left: 50%;
        transition: ${durations.regular};
        opacity: 0;
        width: 0;
        height: 2.25rem;
        content: '';
      }
    }

    ${mq.md} {
      flex-direction: column;

      > a,
      > span {
        width: initial;

        &::after {
          display: none;
        }
      }
    }
  `}
`;

const NonLink = styled.span`
  ${StyledLink.__emotion_styles}

  ${({ theme: { mq } }) => css`
    cursor: pointer;

    ${mq.md} {
      column-gap: 1rem;
    }
  `}
`;

const ListContainer = styled.ul`
  ${({ theme: { colors, mq } }) => css`
    display: flex;
    position: absolute;
    top: calc(100% + 2.25rem);
    left: 2rem;
    flex-direction: column;
    transition: 0.3s;
    opacity: 0;
    border-radius: 0 1rem 1rem;
    box-shadow: 0 0 2.5rem 0 ${colors.black}12;
    background-color: ${colors.white};
    padding: 1rem 0;
    min-width: 40rem;
    list-style: none;
    pointer-events: none;

    ${mq.md} {
      display: grid;
      position: relative;
      top: 0;
      opacity: 1;
      box-shadow: initial;
      background-color: transparent;
      padding: 0;
      width: 100%;
      min-width: initial;
      overflow: hidden;
      pointer-events: initial;

      & > a {
        width: initial;
      }
    }
  `}
`;

const ListLink = styled(Link)`
  ${({ theme: { colors, tp, mq } }) => css`
    ${tp.useFont(fonts => fonts.Montserrat.regular)};

    padding: 1.5rem 4rem;
    line-height: 1.214;
    color: ${colors.black};
    font-size: 1.75rem;

    &:hover,
    &:active {
      color: ${colors.black};
    }

    &:hover {
      background-color: ${colors.backgroundLight};
    }

    ${mq.md} {
      padding: 1rem 2rem;
      color: var(${HEADER_VARIABLES.textColor});

      &:active {
        color: var(${HEADER_VARIABLES.textActiveColor});
      }

      &:hover {
        background-color: transparent;
        color: var(${HEADER_VARIABLES.textColor});
      }
    }
  `}
`;

const StyledChevron = styled(LinkChevron, filterProps('open'))<{
  open: boolean;
}>`
  ${({ theme: { durations, mq }, open }) => css`
    display: none;

    ${mq.md} {
      display: block;
      transform: rotate(${open ? 270 : 90}deg);
      transition: transform ${durations.regular};
      width: auto;
      height: 1.675rem;

      & path {
        stroke: var(${HEADER_VARIABLES.textColor});
      }
    }
  `}
`;

const StyledAnimateHeight = styled(AnimateHeight)`
  ${({ theme: { mq } }) => css`
    ${mq.mdMin} {
      & > div {
        display: contents !important;
      }
    }
  `}
`;

const LinksList: React.FC<ILinksList> = ({ label, url, links, open, onClick }) => {
  return (
    <Container>
      {url ? (
        <Link href={url}>{label}</Link>
      ) : (
        <NonLink onClick={onClick}>
          {label}
          <StyledChevron open={open} />
        </NonLink>
      )}
      <StyledAnimateHeight height={open ? 'auto' : 0}>
        <ListContainer>
          {links.map(({ label, url }, index) => (
            <ListLink key={`${url}${index}`} href={url}>
              {label}
            </ListLink>
          ))}
        </ListContainer>
      </StyledAnimateHeight>
    </Container>
  );
};

export default LinksList;
